import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Tag, Calendar, Percent, DollarSign, Power } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';

const AdminPromoCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    max_uses: '',
    description: ''
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/promo-codes');
      setCodes(res.data || []);
    } catch (err) {
      toast.error('Error cargando códigos');
    } finally {
      setLoading(false);
    }
  };

  const createCode = async () => {
    if (!form.code.trim()) return toast.error('Ingresa un código');
    if (form.discount_value <= 0) return toast.error('Descuento inválido');

    setCreating(true);
    try {
      const payload = {
        ...form,
        discount_value: Number(form.discount_value),
        max_uses: form.max_uses ? Number(form.max_uses) : null
      };
      await api.post('/admin/promo-codes', payload);
      toast.success('Código creado');
      setShowForm(false);
      setForm({ ...form, code: '', description: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (code) => {
    try {
      await api.put(`/admin/promo-codes/${code.code_id}`, { active: !code.active });
      load();
    } catch (err) {
      toast.error('Error');
    }
  };

  const deleteCode = async (code_id) => {
    if (!window.confirm('¿Eliminar este código?')) return;
    try {
      await api.delete(`/admin/promo-codes/${code_id}`);
      toast.success('Eliminado');
      load();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) return <div className="text-center py-10">Cargando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold">Códigos Promocionales</h3>
          <p className="text-sm text-gray-500">{codes.length} código(s) totales</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#E6202E] hover:bg-[#D31522]"
          data-testid="create-promo-toggle"
        >
          <Plus className="w-4 h-4 mr-1" /> {showForm ? 'Cancelar' : 'Nuevo Código'}
        </Button>
      </div>

      {showForm && (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Código *</label>
              <Input
                placeholder="VERANO2026"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                data-testid="promo-form-code"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Descripción (opcional)</label>
              <Input
                placeholder="Descuento verano"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Tipo de descuento *</label>
              <select
                value={form.discount_type}
                onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="promo-form-type"
              >
                <option value="percentage">Porcentaje (%)</option>
                <option value="fixed">Monto fijo (CLP)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">
                Valor * ({form.discount_type === 'percentage' ? '%' : '$ CLP'})
              </label>
              <Input
                type="number"
                value={form.discount_value}
                onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                data-testid="promo-form-value"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Fecha de inicio *</label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Fecha de término *</label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Máximo de usos (vacío = ilimitado)</label>
              <Input
                type="number"
                placeholder="100"
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              />
            </div>
          </div>
          <Button onClick={createCode} disabled={creating} className="w-full bg-[#E6202E] hover:bg-[#D31522]" data-testid="promo-form-submit">
            {creating ? 'Creando...' : 'Crear Código'}
          </Button>
        </div>
      )}

      {codes.length === 0 ? (
        <p className="text-center text-gray-400 py-10">No hay códigos creados aún</p>
      ) : (
        <div className="space-y-2">
          {codes.map((c) => {
            const expired = new Date(c.end_date) < new Date();
            return (
              <div key={c.code_id} className={`p-4 rounded-xl border-2 ${c.active && !expired ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag className="w-4 h-4 text-[#E6202E]" />
                      <span className="font-mono font-bold text-lg" data-testid={`promo-code-${c.code_id}`}>{c.code}</span>
                      {c.active && !expired ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Activo</span>
                      ) : expired ? (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Expirado</span>
                      ) : (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Desactivado</span>
                      )}
                    </div>
                    {c.description && <p className="text-sm text-gray-600 mt-1">{c.description}</p>}
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        {c.discount_type === 'percentage' ? <Percent className="w-3 h-3" /> : <DollarSign className="w-3 h-3" />}
                        {c.discount_value}{c.discount_type === 'percentage' ? '%' : ' CLP'} descuento
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {c.start_date} → {c.end_date}
                      </span>
                      <span>📊 {c.current_uses}/{c.max_uses || '∞'} usos</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => toggleActive(c)} data-testid={`toggle-promo-${c.code_id}`}>
                      <Power className="w-4 h-4 mr-1" />
                      {c.active ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteCode(c.code_id)} className="text-red-500 border-red-300">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminPromoCodes;
